<template>
  <div class="i-link-accounts">
    <div class="label"><img :src="iconGoogle" /> Google Account</div>
    <div class="field">
      <iBaseButton
        v-if="!userStore.isGoogleLinked"
        v-bind="OTHER_BUTTON_STYLE"
        text="Link"
        @clicked="goToGoogleOauth()"
      />
      <iBaseButton
        v-if="userStore.isGoogleLinked"
        v-bind="RED_BUTTON_STYLE"
        :text="data.isUnlinking ? 'Unlinking...' : 'Unlink'"
        :is-disabled="data.isUnlinking"
        @clicked="unlinkGoogle()"
      />
      <div
        v-if="data.oAuthFailed"
        class="error"
      >
        Your attempt to link your account failed, please try again or contact support
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { bittersweet500, black600, white } from "@/styles/colors"

import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconGoogle from "@/assets/icons/icon-google-color.svg"

// STORES AND ROUTERS
const userStore = useUserStore()
const router = useRouter()
const activeRoute = useRoute()

// COSTANTS
const RED_BUTTON_STYLE = {
  backgroundColor: bittersweet500,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "fit-content",
  padding: "10px 16px",
  size: "small"
}
const OTHER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: black600,
  width: "fit-content",
  padding: "10px 16px",
  size: "small",
  version: "outline"
}

// DATA
const data = reactive({
  isUnlinking: false,
  oAuthFailed: false
})

// FUNCTIONS
const goToGoogleOauth = () => {
  api.auth.goToGoogleOauth(
    window.location.pathname + window.location.search, // redirect after successfully linking
    window.location.pathname + window.location.search // redirect after failure
  )
}

const unlinkGoogle = async () => {
  data.isUnlinking = true
  try {
    await api.user.putUnlinkGoogle()
    await userStore.loadUserData()
  } catch (err) {
    console.error("Failed to unlink Google Account", err)
  }
  data.isUnlinking = false
}

// LIFECYCLE HOOKS
onMounted(() => {
  // Show oAuthFailed error for 20 seconds if found in query
  const oAuthFailed = activeRoute.query["oauthFailed"] as string
  if (oAuthFailed === "true") {
    data.oAuthFailed = true
    setTimeout(() => {
      data.oAuthFailed = false
    }, 20000)
    router.replace({ query: { ...activeRoute.query, oauthFailed: undefined, oauthErrorMessage: undefined } })
  }
})
</script>

<style scoped lang="scss">
.i-link-accounts {
  @include flex(row, start, center);

  .label {
    @include text-sm;

    font-weight: 600;
    width: 170px;
    flex: 0 0 170px;

    @media screen and (min-width: $breakpoint-md) {
      width: 250px;
      flex: 0 0 250px;
    }

    > img {
      margin-right: 6px;
    }
  }

  .field {
    @include flex(column, start, start);

    .error {
      @include text-xs;

      margin-top: 5px;
      color: $bittersweet600;
    }
  }
}
</style>
